import React from 'react';
import StudentTableRow from './StudentTableRow';

/**
 * StudentRosterTable
 *
 * Main table component for displaying the student roster.
 * Features sortable columns and header checkbox for bulk selection.
 */

// Column definitions
const COLUMNS = [
  { id: 'checkbox', label: '', sortable: false, width: '48px' },
  { id: 'uuid', label: 'Students', sortable: true },
  { id: 'subject', label: 'Subject', sortable: true },
  { id: 'tutor', label: 'Tutor', sortable: false },
  { id: 'sessions', label: 'Sessions', sortable: true, width: '100px' },
  { id: 'assigned_at', label: 'Assigned Date', sortable: true },
  { id: 'actions', label: '', sortable: false, width: '100px' }
];

const StudentRosterTable = ({
  students,
  tutorInfo,
  sortConfig,
  onSort,
  selectedIds,
  onToggleSelection,
  onToggleAll,
  isAllSelected,
  isPartiallySelected,
  onViewDetails,
  isAllMode = false,
  isAdmin = false,
  tutors = [],
  onTutorAssignment,
  onStartMatch,
  isMatchingActive = false,
  matchingStudentUuid = null
}) => {
  // In "all" mode, tutor column should be sortable
  const columns = COLUMNS.map(col => {
    if (col.id === 'tutor' && isAllMode) {
      return { ...col, sortable: true };
    }
    return col;
  });
  // Render sort indicator
  const renderSortIndicator = (columnId) => {
    if (sortConfig.field !== columnId) {
      return (
        <svg className="sort-icon sort-icon--inactive" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 2L9 5H3L6 2Z" fill="currentColor" opacity="0.3" />
          <path d="M6 10L3 7H9L6 10Z" fill="currentColor" opacity="0.3" />
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg className="sort-icon sort-icon--asc" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 2L9 5H3L6 2Z" fill="currentColor" />
      </svg>
    ) : (
      <svg className="sort-icon sort-icon--desc" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 10L3 7H9L6 10Z" fill="currentColor" />
      </svg>
    );
  };

  // Handle header cell click for sorting
  const handleHeaderClick = (column) => {
    if (column.sortable && onSort) {
      onSort(column.id);
    }
  };

  // Empty state
  if (students.length === 0) {
    return (
      <div className="roster-table__empty">
        <div className="roster-table__empty-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect x="8" y="12" width="32" height="24" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M8 18H40" stroke="currentColor" strokeWidth="2" />
            <path d="M16 24H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M16 30H28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h3 className="roster-table__empty-title">No Students Found</h3>
        <p className="roster-table__empty-text">
          No students match your current filters, or no students are assigned yet.
        </p>
      </div>
    );
  }

  return (
    <div className="roster-table-wrapper">
      <table className="roster-table">
        <thead className="roster-table__header">
          <tr>
            {columns.map(column => (
              <th
                key={column.id}
                className={`roster-table__header-cell roster-table__header-cell--${column.id} ${
                  column.sortable ? 'roster-table__header-cell--sortable' : ''
                } ${sortConfig.field === column.id ? 'roster-table__header-cell--sorted' : ''}`}
                style={column.width ? { width: column.width } : undefined}
                onClick={() => handleHeaderClick(column)}
              >
                {column.id === 'checkbox' ? (
                  <label className="roster-checkbox roster-checkbox--header">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={input => {
                        if (input) {
                          input.indeterminate = isPartiallySelected && !isAllSelected;
                        }
                      }}
                      onChange={onToggleAll}
                      aria-label="Select all students"
                    />
                    <span className="roster-checkbox__mark"></span>
                  </label>
                ) : (
                  <div className="roster-table__header-content">
                    <span>{column.label}</span>
                    {column.sortable && renderSortIndicator(column.id)}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="roster-table__body">
          {students.map(student => (
            <StudentTableRow
              key={student.uuid}
              student={student}
              tutorName={isAllMode ? student.assigned_tutor_name : tutorInfo?.name}
              isSelected={selectedIds.has(student.uuid)}
              onToggleSelection={() => onToggleSelection(student.uuid)}
              onViewDetails={() => onViewDetails(student)}
              isAllMode={isAllMode}
              isAdmin={isAdmin}
              tutors={tutors}
              onTutorAssignment={onTutorAssignment}
              onStartMatch={onStartMatch}
              isMatchingActive={isMatchingActive}
              matchingStudentUuid={matchingStudentUuid}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StudentRosterTable;
